import { useCallback, useState } from 'react'
import { Layers, Plus, Trash2 } from 'lucide-react'

import { MasterFormModal } from '@/components/masters/MasterFormModal'
import { MasterServerTable } from '@/components/masters/MasterServerTable'
import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createServiceCategory,
  deleteServiceCategory,
  fetchServiceCategoriesList,
  fetchServiceCategoriesPage,
  GENDER_SCOPE_OPTIONS,
  updateServiceCategory,
} from '@/lib/api/serviceCategories'
import { cn } from '@/lib/utils'

function emptyForm() {
  return {
    name: '',
    gender_scope: 'ladies',
    sort: 0,
    colour: '#7C3AED',
    bookable_online_default: true,
    is_active: true,
  }
}

/**
 * @param {import('@/types/api').ServiceCategory} category
 */
function categoryToForm(category) {
  return {
    name: category.name ?? '',
    gender_scope: category.gender_scope ?? 'ladies',
    sort: category.sort ?? 0,
    colour: category.colour ?? '#7C3AED',
    bookable_online_default: Boolean(category.bookable_online_default),
    is_active: Boolean(category.is_active),
  }
}

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name', getValue: (row) => row.name },
  { key: 'gender_scope', label: 'Gender scope', getValue: (row) => row.gender_scope },
  { key: 'sort', label: 'Sort', getValue: (row) => row.sort },
  { key: 'services_count', label: 'Services', getValue: (row) => row.services_count ?? 0 },
  { key: 'is_active', label: 'Active', getValue: (row) => (row.is_active ? 'yes' : 'no') },
]

export function ServiceCategoriesPage() {
  const { activeBranchId } = useBranch()
  const [modalOpen, setModalOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState(emptyForm())
  const [reloadKey, setReloadKey] = useState(0)

  const fetchPage = useCallback((params) => fetchServiceCategoriesPage(params), [])

  function openCreate() {
    setCreating(true)
    setSelectedId(null)
    setSelectedCategory(null)
    setForm(emptyForm())
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  /**
   * @param {import('@/types/api').ServiceCategory} category
   */
  function openEdit(category) {
    setCreating(false)
    setSelectedId(category.id)
    setSelectedCategory(category)
    setForm(categoryToForm(category))
    setError('')
    setSuccess('')
    setModalOpen(true)
  }

  function closeModal() {
    if (submitting || deleting) return
    setModalOpen(false)
    setError('')
    setSuccess('')
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
    setSuccess('')
  }

  async function handleDelete() {
    if (!selectedId || creating) {
      return
    }

    const categoryName = form.name.trim() || 'this category'
    const servicesCount = selectedCategory?.services_count ?? 0
    const servicesNote =
      servicesCount > 0
        ? ` This category still has ${servicesCount} service(s) — delete or move them first.`
        : ''

    if (
      !window.confirm(
        `Delete "${categoryName}"? This cannot be undone. The category will be removed from POS and booking menus.${servicesNote}`,
      )
    ) {
      return
    }

    setDeleting(true)
    setError('')
    setSuccess('')

    try {
      await deleteServiceCategory(selectedId)
      setReloadKey((current) => current + 1)
      setSuccess('Category deleted.')
      setModalOpen(false)
    } catch (err) {
      setError(
        getFieldError(err, 'category') ??
          err?.response?.data?.message ??
          'Unable to delete category.',
      )
    } finally {
      setDeleting(false)
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setSuccess('')

    const payload = {
      ...form,
      sort: Number(form.sort) || 0,
      colour: form.colour || null,
    }

    try {
      if (creating) {
        await createServiceCategory(payload)
        setSuccess('Category created.')
      } else if (selectedId) {
        await updateServiceCategory(selectedId, payload)
        setSuccess('Category saved.')
      }
      setReloadKey((current) => current + 1)
      setModalOpen(false)
    } catch (err) {
      const message =
        getFieldError(err, 'name') ??
        getFieldError(err, 'gender_scope') ??
        err?.response?.data?.message ??
        'Unable to save category.'
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-[1440px] px-4 py-8 lg:px-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Layers className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Service categories</h1>
            <p className="text-sm text-muted-foreground">
              Group services by menu section — gender scope, colour, and online booking default.
            </p>
          </div>
        </div>
        <Button type="button" variant="outline" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New category
        </Button>
      </div>

      {success && !modalOpen ? (
        <p className="mb-4 text-sm text-primary" role="status">
          {success}
        </p>
      ) : null}

      {error && !modalOpen ? (
        <p className="mb-4 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <MasterServerTable
        key={activeBranchId}
        fetchPage={fetchPage}
        reloadKey={reloadKey}
        getRowKey={(category) => category.id}
        onEdit={openEdit}
        emptyLabel="No categories yet."
        searchPlaceholder="Search categories…"
        exportColumns={EXPORT_COLUMNS}
        exportFilename="service-categories.csv"
        fetchAllForExport={(params) => fetchServiceCategoriesList(params)}
        columns={[
          {
            key: 'name',
            label: 'Name',
            sortable: true,
            render: (row) => (
              <span className="inline-flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: row.colour ?? '#7C3AED' }}
                  aria-hidden
                />
                {row.name}
              </span>
            ),
          },
          {
            key: 'gender_scope',
            label: 'Scope',
            render: (row) =>
              GENDER_SCOPE_OPTIONS.find((option) => option.value === row.gender_scope)?.label ??
              row.gender_scope,
          },
          {
            key: 'services_count',
            label: 'Services',
            render: (row) => row.services_count ?? 0,
          },
          {
            key: 'sort',
            label: 'Sort',
            sortable: true,
            render: (row) => row.sort ?? 0,
          },
          {
            key: 'is_active',
            label: 'Status',
            render: (row) => (row.is_active ? 'Active' : 'Inactive'),
          },
        ]}
      />

      <MasterFormModal
        open={modalOpen}
        onClose={closeModal}
        title={creating ? 'New category' : 'Edit category'}
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="category-name" className="mb-1.5 block text-sm font-medium">
              Name
            </label>
            <input
              id="category-name"
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={cn(
                'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                'focus:outline-none focus:ring-2 focus:ring-ring',
              )}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="category-gender" className="mb-1.5 block text-sm font-medium">
                Gender scope
              </label>
              <select
                id="category-gender"
                value={form.gender_scope}
                onChange={(e) => updateField('gender_scope', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                {GENDER_SCOPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="category-sort" className="mb-1.5 block text-sm font-medium">
                Sort order
              </label>
              <input
                id="category-sort"
                type="number"
                min={0}
                value={form.sort}
                onChange={(e) => updateField('sort', e.target.value)}
                className={cn(
                  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              />
            </div>
            <div>
              <label htmlFor="category-colour" className="mb-1.5 block text-sm font-medium">
                Colour
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="category-colour"
                  type="color"
                  value={form.colour}
                  onChange={(e) => updateField('colour', e.target.value)}
                  className="h-11 w-14 cursor-pointer rounded-xl border border-border/80 bg-card p-1"
                />
                <input
                  type="text"
                  value={form.colour}
                  onChange={(e) => updateField('colour', e.target.value)}
                  className={cn(
                    'h-11 min-w-0 flex-1 rounded-xl border border-border/80 bg-card px-4 text-sm',
                    'focus:outline-none focus:ring-2 focus:ring-ring',
                  )}
                />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.bookable_online_default}
              onChange={(e) => updateField('bookable_online_default', e.target.checked)}
              className="rounded border-border"
            />
            Bookable online by default
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => updateField('is_active', e.target.checked)}
              className="rounded border-border"
            />
            Category is active
          </label>

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}

          <div className="flex flex-wrap justify-between gap-2">
            {!creating && selectedId ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleDelete}
                disabled={deleting || submitting}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? 'Deleting…' : 'Delete category'}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={closeModal} disabled={submitting || deleting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || deleting}>
                {submitting ? 'Saving…' : creating ? 'Create category' : 'Save category'}
              </Button>
            </div>
          </div>
        </form>
      </MasterFormModal>
    </div>
  )
}
